import axios from 'axios';

// 创建axios实例
// 在开发环境使用代理（package.json中配置了proxy），生产环境使用环境变量或默认URL
const getBaseURL = () => {
  // 优先使用环境变量
  if (process.env.REACT_APP_API_URL) {
    console.log('[API] Using REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  // Always use relative path — nginx on www.sagax.cn proxies /api/ to backend
  // In development, React dev server proxy handles it; in production, nginx handles it
  console.log('[API] Using relative path (same-origin proxy)');
  return '';
};

// 认证接口固定使用主后端，避免被市场切换(baseURL)覆盖
const getAuthBaseURL = () => {
  if (process.env.REACT_APP_AUTH_API_URL) {
    console.log('[API] Using REACT_APP_AUTH_API_URL:', process.env.REACT_APP_AUTH_API_URL);
    return process.env.REACT_APP_AUTH_API_URL;
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return '';
};

const baseURL = getBaseURL();
console.log('[API] Base URL configured:', baseURL || '(empty - using proxy)');
const authBaseURL = getAuthBaseURL();
console.log('[API] Auth Base URL configured:', authBaseURL || '(empty - using proxy)');

const createApiClient = (clientBaseURL) => axios.create({
  baseURL: clientBaseURL,
  timeout: 120000, // 增加到120秒，AI分析需要更长时间
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

const api = createApiClient(baseURL);
const authApi = createApiClient(authBaseURL);

// 跨市场共享 API 客户端 — baseURL 固定为空（走 nginx 代理到主后端）
// 用于新闻、sagax-agent、量化专区、设置等不随市场切换的模块
const sharedApi = createApiClient('');

// 动态设置API基础URL（用于市场切换）
export const setApiBaseURL = (newBaseURL) => {
  api.defaults.baseURL = newBaseURL;
  console.log('[API] Base URL updated to:', newBaseURL || '(empty - using proxy)');
  // sharedApi 不受市场切换影响，始终走主后端
};

const attachInterceptors = (client) => {
  // 请求拦截器
  client.interceptors.request.use(
    (config) => {
      // 可以在这里添加认证token
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // 响应拦截器
  client.interceptors.response.use(
    (response) => response.data,
    (error) => {
      console.error('API Error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });

      // 处理网络错误
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        console.error('网络连接错误，请检查：');
        console.error('1. 后端服务是否运行在 http://localhost:5000');
        console.error('2. 防火墙是否阻止了连接');
        console.error('3. 浏览器是否阻止了跨域请求');
      }

      // 处理401未授权错误 - 重定向到登录页
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // 避免在登录页面重定向
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    }
  );
};

attachInterceptors(api);
attachInterceptors(authApi);
attachInterceptors(sharedApi);

// 股票相关API
export const stockAPI = {
  // 获取股票列表
  getStockList: (params = {}) => {
    return api.get('/api/stock/list', { params });
  },

  // 搜索股票 - 使用新的AI诊断API
  searchStocks: async (keyword, limit = 20) => {
    const response = await api.get('/api/ai-diagnosis/search/stocks', { 
      params: { q: keyword, limit } 
    });
    // 返回data字段，因为响应拦截器已经处理了response.data
    return response.data || [];
  },

  // 获取股票基本信息 (中文名 / 行业 / 上市日期等)
  getStockBasic: (tsCode) => {
    return api.get(`/api/stock/${tsCode}/basic`);
  },

  // 获取股票日行情数据
  getStockDaily: (tsCode, params = {}) => {
    return api.get(`/api/stock/${tsCode}/daily`, { params });
  },

  // 获取股票技术指标
  getStockIndicators: (tsCode, params = {}) => {
    return api.get(`/api/stock/${tsCode}/indicators`, { params });
  },

  // 获取股票最新因子分数
  getStockFactorsLatest: (tsCode) => {
    return api.get(`/api/stock/${tsCode}/factors/latest`);
  },

  // 同步股票数据
  syncStockData: () => {
    return api.post('/api/stock/sync');
  },
};

// Agentic 信号墙 (F9 终端页) — 走 sagax-agent 服务 (nginx → 5150)
export const agenticAPI = {
  list:    () => sharedApi.get('/api/agents/monitor/list'),
  signals: (agentId, limit = 50) => sharedApi.get('/api/agents/monitor/signals', { params: { agent_id: agentId, limit } }),
  stats:   (agentId) => sharedApi.get('/api/agents/monitor/stats', { params: { agent_id: agentId } }),
  activeSessions: (params = {}) => sharedApi.get('/api/agents/monitor/active-sessions', { params }),
  // 历史会话（TCHouse chat_sessions，可分页，重启后依然可见）
  history:        (params = {}) => sharedApi.get('/api/agents/monitor/history', { params }),
};

// 宏观仪表板 (F7 终端页)
export const macroAPI = {
  summary:     () => api.get('/api/macro/summary'),
  rates:       () => api.get('/api/macro/rates'),
  yieldCurve:  () => api.get('/api/macro/yield-curve'),
  fx:          () => api.get('/api/macro/fx'),
  commodities: () => api.get('/api/macro/commodities'),
  releases:    () => api.get('/api/macro/releases'),
  global_:     () => api.get('/api/macro/global'),
};

// AI诊断相关API
export const aiDiagnosisAPI = {
  // 智能搜索股票
  searchStocks: (keyword, limit = 20) => {
    return api.get('/api/ai-diagnosis/search/stocks', { 
      params: { q: keyword, limit } 
    }).then(response => {
      // 响应拦截器已经处理了response.data，所以直接返回
      return response.data || [];
    });
  },

  // 获取搜索建议
  getSearchSuggestions: (keyword, limit = 10) => {
    return api.get('/api/ai-diagnosis/search/suggestions', { 
      params: { q: keyword, limit } 
    }).then(response => {
      return response.data || [];
    });
  },

  // 获取市场概览
  getMarketOverview: () => {
    return api.get('/api/ai-diagnosis/search/market-overview');
  },

  // 按行业搜索
  searchByIndustry: (industry, limit = 50) => {
    return api.get('/api/ai-diagnosis/search/by-industry', { 
      params: { industry, limit } 
    }).then(response => {
      return response.data || [];
    });
  },

  // 按地区搜索
  searchByArea: (area, limit = 50) => {
    return api.get('/api/ai-diagnosis/search/by-area', { 
      params: { area, limit } 
    }).then(response => {
      return response.data || [];
    });
  },

  // 估值分析
  analyzeValuation: (tsCode, mode = 'thinking', customPrompt = '') => {
    // 根据模式设置不同的超时时间
    const timeout = mode === 'thinking' ? 600000 : 60000; // 深度思考10分钟，快速回复60秒
    return api.post('/api/ai-diagnosis/valuation/analyze', { 
      ts_code: tsCode,
      mode: mode,
      custom_prompt: customPrompt
    }, { timeout });
  },


  // 综合评估分析
  comprehensiveAnalyze: (tsCode, mode = 'thinking', customPrompt = '') => {
    // 根据模式设置不同的超时时间
    const timeout = mode === 'thinking' ? 600000 : 60000; // 深度思考10分钟，快速回复60秒
    return api.post('/api/ai-diagnosis/comprehensive-evaluation/analyze', { 
      ts_code: tsCode,
      mode: mode,
      custom_prompt: customPrompt
    }, { timeout });
  },

  // 批量分析
  batchAnalyze: (tsCodes, analysisTypes = ['valuation', 'trend', 'comprehensive']) => {
    return api.post('/api/ai-diagnosis/batch/analyze', { 
      ts_codes: tsCodes, 
      analysis_types: analysisTypes 
    });
  },

  // 健康检查
  healthCheck: () => {
    return api.get('/api/ai-diagnosis/health');
  },

  // PDF报告生成
  generateValuationReport: (tsCode) => {
    return api.get(`/api/ai-diagnosis/reports/valuation/${tsCode}`);
  },

  generateTrendReport: (tsCode) => {
    return api.get(`/api/ai-diagnosis/reports/trend/${tsCode}`);
  },

  generateComprehensiveReport: (tsCode) => {
    return api.get(`/api/ai-diagnosis/reports/comprehensive/${tsCode}`);
  },

  downloadReport: (filename) => {
    return api.get(`/api/ai-diagnosis/reports/download/${filename}`, {
      responseType: 'blob'
    });
  }
};

// 市场相关API
export const marketAPI = {
  // 获取市场概览
  getMarketOverview: () => {
    return api.get('/api/market/overview');
  },

  // 获取主要指数
  getMajorIndices: () => {
    return api.get('/api/market/indices');
  },

  // 获取板块表现
  getSectorPerformance: () => {
    return api.get('/api/market/sectors');
  },

  // 获取涨幅榜
  getGainers: () => {
    return api.get('/api/market/gainers');
  },

  // 获取跌幅榜
  getLosers: () => {
    return api.get('/api/market/losers');
  },

  // 获取市场扫描数据
  getMarketScan: () => {
    return api.get('/api/market/market-scan');
  },

  // 获取指数历史价格时间序列
  getIndexHistory: (tsCode, startDate, endDate) => {
    return api.get(`/api/market/index/${tsCode}/history`, {
      params: { start_date: startDate, end_date: endDate }
    });
  },

  // 获取指数基本信息
  getIndexInfo: (tsCode) => {
    return api.get(`/api/market/index/${tsCode}/info`);
  },
};

// Crypto 市场 API — 走 sharedApi (nginx /api/crypto/ → 5002)
export const cryptoAPI = {
  getOverview: () => sharedApi.get('/api/crypto/market/overview'),
  getSectors: () => sharedApi.get('/api/crypto/market/sectors'),
  getSectorDetail: (name) => sharedApi.get(`/api/crypto/market/sector/${encodeURIComponent(name)}`),
  getGainers: () => sharedApi.get('/api/crypto/market/gainers'),
  getLosers: () => sharedApi.get('/api/crypto/market/losers'),
  getSymbols: (params) => sharedApi.get('/api/crypto/symbols', { params }),
  getSymbolInfo: (symbol) => sharedApi.get(`/api/crypto/symbol/${encodeURIComponent(symbol)}/info`),
  getOHLCV: (symbol, params) => sharedApi.get(`/api/crypto/symbol/${encodeURIComponent(symbol)}/ohlcv`, { params }),
  getKeyTickers: () => sharedApi.get('/api/crypto/key-tickers'),
  getConcepts: () => sharedApi.get('/api/crypto/concepts'),
  getSymbolDetail: (symbol) => sharedApi.get(`/api/crypto/symbol/${encodeURIComponent(symbol)}/detail`),
  getSymbolOHLCV: (symbol, params) => sharedApi.get(`/api/crypto/symbol/${encodeURIComponent(symbol)}/ohlcv`, { params }),
};

// 用户相关API
export const userAPI = {
  // 用户登录
  login: (credentials) => {
    return authApi.post('/api/auth/login', credentials);
  },

  // 用户注册
  register: (userData) => {
    return authApi.post('/api/auth/register', userData);
  },

  // 用户登出
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return Promise.resolve({ success: true });
  },

  // 获取当前用户信息
  getCurrentUser: () => {
    return authApi.get('/api/auth/me');
  },

  // 验证token
  verifyToken: (token) => {
    return authApi.post('/api/auth/verify', { token });
  },

  // Google OAuth 登录
  googleLogin: (credential) => {
    return authApi.post('/api/auth/google', { credential });
  },

  // 发送短信验证码
  sendSmsCode: (phone) => {
    return authApi.post('/api/auth/sms/send', { phone });
  },

  // 短信验证码登录
  smsLogin: (phone, code) => {
    return authApi.post('/api/auth/sms/login', { phone, code });
  },

  // 微信 OAuth 登录
  getWechatAuthUrl: () => {
    return authApi.get('/api/auth/wechat/url');
  },
  wechatLogin: (code, state) => {
    return authApi.post('/api/auth/wechat', { code, state });
  },

  // 更新个人资料
  updateProfile: (data) => {
    return authApi.put('/api/auth/profile', data);
  },

  // 修改密码
  changePassword: (data) => {
    return authApi.post('/api/auth/change-password', data);
  },
};

// API Key 管理 (跨市场共享)
export const apiKeyAPI = {
  create: (data) => sharedApi.post('/api/keys/create', data),
  list: () => sharedApi.get('/api/keys/list'),
  delete: (keyId) => sharedApi.delete(`/api/keys/${keyId}/delete`),
  update: (keyId, data) => sharedApi.put(`/api/keys/${keyId}/update`, data),
  usage: (keyId, days = 7) => sharedApi.get(`/api/keys/${keyId}/usage`, { params: { days } }),
};

// 健康检查
export const healthCheck = () => {
  return api.get('/api/health');
};

// ============================================================================
// 自适应组合回测 API
// ============================================================================

/**
 * 运行自适应组合回测
 * @param {Object} config - 回测配置
 * @returns {Promise} 回测结果
 */
export const runAdaptiveBacktest = (config) => {
  return api.post('/api/adaptive/backtest/run', config, { timeout: 300000 });
};

/**
 * 获取已保存的回测结果
 * @param {string} runId - 回测运行ID
 * @returns {Promise} 回测结果
 */
export const getAdaptiveBacktestResult = (runId) => {
  return api.get(`/api/adaptive/backtest/result/${runId}`);
};

export const adaptiveBacktestAPI = {
  run: runAdaptiveBacktest,
  getResult: getAdaptiveBacktestResult,
};

// ============================================================================
// 双策略线 API (Dual-Line)
// ============================================================================

export const getPVValidity = () => api.get('/api/adaptive/pv-validity/latest');
export const getPVValidityHistory = (params) => api.get('/api/adaptive/pv-validity/history', { params });
export const getMechanismRegime = () => api.get('/api/adaptive/mechanism-regime/latest');
export const getMechanismRegimeHistory = (params) => api.get('/api/adaptive/mechanism-regime/history', { params });
export const getDecisionContracts = () => api.get('/api/adaptive/decision-contracts');
export const evaluateContracts = () => api.post('/api/adaptive/decision-contracts/evaluate');
export const getDecisionContract = (id) => api.get(`/api/adaptive/decision-contracts/${id}`);
export const getTradeStructures = () => api.get('/api/adaptive/trade-structures');
export const getSwitchingDecision = () => api.get('/api/adaptive/switching/latest');
export const runDualLinePipeline = () => api.post('/api/adaptive/switching/run');
export const getStrategyLines = () => api.get('/api/adaptive/strategy-lines');

// ============================================================================
// 多智能体选股策略 API (Agent Strategy)
// ============================================================================

export const agentStrategyAPI = {
  getDefaultSpec: () => api.get('/api/adaptive/agent/strategy-specs/default'),
  validateSpec: (spec) => api.post('/api/adaptive/agent/strategy-specs/validate', spec),
  run: (data) => api.post('/api/adaptive/agent/run', data),
  runBacktest: (params) => api.post('/api/adaptive/agent/backtest/run', params, { timeout: 600000 }),
  getLatestPack: () => api.get('/api/adaptive/agent/decision-pack/latest'),
  getAgentDominance: () => api.get('/api/adaptive/agent/agents/latest'),
  getRegime: () => api.get('/api/adaptive/agent/regime/latest'),
  getScores: (params) => api.get('/api/adaptive/agent/scores', { params }),
  getFalsification: () => api.get('/api/adaptive/agent/falsification'),
  // LLM多智能体实时决策
  runLLMDecision: (params) => api.post('/api/adaptive/agent/llm-decision/run', params, { timeout: 600000 }),
  getLatestLLMDecision: () => api.get('/api/adaptive/agent/llm-decision/latest'),
  getLLMDecisionStatus: () => api.get('/api/adaptive/agent/llm-decision/status'),
  getLLMDecisionHistory: (params) => api.get('/api/adaptive/agent/llm-decision/history', { params }),
  getLLMDecisionDetail: (runId) => api.get(`/api/adaptive/agent/llm-decision/${runId}`),
};

// ============================================================================
// Factor Agent API (LLM-generated factor generation & review)
// ============================================================================

export const factorAgentAPI = {
  generate: (params) => api.post('/api/factor-agent/generate', params, { timeout: 300000 }),
  listReviews: () => api.get('/api/factor-agent/reviews'),
  getReview: (filename) => api.get(`/api/factor-agent/reviews/${filename}`),
  approveReview: (filename) => api.post(`/api/factor-agent/reviews/${filename}/approve`),
  rejectReview: (filename, reason) => api.post(`/api/factor-agent/reviews/${filename}/reject`, { reason }),
  listRegistered: () => api.get('/api/factor-agent/registered'),
};

// ============================================================================
// Chat API (Cursor-style conversational interface)
// ============================================================================

export const chatAPI = {
  listSessions: () => sharedApi.get('/api/chat/sessions'),
  getSession: (sessionId) => sharedApi.get(`/api/chat/sessions/${sessionId}`),
  deleteSession: (sessionId) => sharedApi.delete(`/api/chat/sessions/${sessionId}`),
  getPreferences: () => sharedApi.get('/api/chat/preferences'),
  savePreferences: (prefs) => sharedApi.put('/api/chat/preferences', prefs),
  // Note: streaming is handled via fetch in useChat hook, not axios
};

// ============================================================================
// News Intelligence API (新闻智能分析)
// ============================================================================

export const newsIntelAPI = {
  listArticles: (params) => sharedApi.get('/api/news-intel/articles', { params }),
  getArticle: (articleId) => sharedApi.get(`/api/news-intel/articles/${articleId}`),
  listMapArticles: (params) => sharedApi.get('/api/news-intel/articles/map', { params }),
  listBreaking: (params) => sharedApi.get('/api/news-intel/breaking', { params }),
  listSignals: (params) => sharedApi.get('/api/news-intel/signals', { params }),
  getStockSignals: (tsCode, params) => sharedApi.get(`/api/news-intel/signals/${tsCode}`, { params }),
  triggerAnalysis: () => sharedApi.post('/api/news-intel/analyze'),
  getDashboard: () => sharedApi.get('/api/news-intel/dashboard'),
  listSources: () => sharedApi.get('/api/news-intel/sources'),
  refresh: () => sharedApi.post('/api/news-intel/refresh'),
};

export default api;
export { sharedApi };
